import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { ArrowRight, Check, Star } from "lucide-react";
import { SiLinkedin } from "react-icons/si";
import { Helmet } from "react-helmet-async";
import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { WebsiteHeader } from "@/components/layout/website-header";
import { WebsiteFooter } from "@/components/layout/website-footer";
import { SaaSGamaLogo } from "@/components/ui/saasgama-logo";

interface PricingPlan {
  id: string;
  name: string;
  description: string;
  features: string[];
  isPopular: boolean;
  prices: {
    month?: {
      id: string;
      amount: number;
      currency: string;
      interval: string;
    };
    year?: {
      id: string;
      amount: number;
      currency: string;
      interval: string;
    };
  };
}

const pricingFaqs = [
  { q: "How much does SaaS management software cost?", a: "SaaS management software pricing typically depends on the number of applications, users, and features required. Businesses can choose a plan that matches their software ecosystem and management needs." },
  { q: "Is SaaS management software worth the investment?", a: "Yes. Organizations often reduce software waste, optimize license usage, and improve spending visibility, helping them gain greater value from their SaaS investments." },
  { q: "What features are included in a SaaS management platform?", a: "Common features include subscription tracking, license management, renewal monitoring, spend analytics, software usage insights, vendor management, and centralized reporting." },
  { q: "How can SaaS management software help reduce software costs?", a: "By identifying unused licenses, duplicate applications, underutilized subscriptions, and upcoming renewals, businesses can make informed decisions to reduce unnecessary spending." },
  { q: "Can SaaS management software help track software renewals?", a: "Yes. Automated renewal tracking and alerts help organizations stay ahead of contract deadlines and avoid unexpected charges or missed renewals." },
  { q: "How do businesses optimize SaaS spending?", a: "Businesses optimize SaaS spending by monitoring software usage, eliminating unused licenses, consolidating duplicate tools, and gaining visibility into subscription costs across teams." },
  { q: "Which pricing plan is best for growing businesses?", a: "The best plan is one that scales with business growth, providing visibility into software usage, license management, spend tracking, and renewal oversight as needs evolve." },
  { q: "Can SaaS management software replace spreadsheets?", a: "Yes. A centralized platform provides real-time software visibility, automated tracking, and reporting capabilities that are difficult to manage efficiently with spreadsheets alone." },
  { q: "How do companies track software usage and costs?", a: "Organizations use SaaS management platforms to monitor application usage, license allocation, subscription expenses, and software adoption across departments from a single dashboard." },
  { q: "Why do businesses invest in SaaS management software?", a: "Businesses invest in SaaS management software to gain control over software subscriptions, improve operational efficiency, optimize software spending, and make data-driven technology decisions." },
];

export default function PricingPage() {
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');

  const navigate = (path: string) => {
    window.location.href = path;
  };

  const { data: pricingPlans = [] } = useQuery({
    queryKey: ['/api/pricing-plans'],
  });

  const { data: publicSettings } = useQuery({
    queryKey: ["/api/system-settings"],
    queryFn: async () => {
      const res = await fetch("/api/system-settings");
      if (!res.ok) throw new Error("Failed to load settings");
      return res.json();
    }
  });

  const showFaq = publicSettings?.showLandingFaq !== false;

  const handleGetStarted = (plan?: PricingPlan) => {
    if (plan) {
      navigate(`/register?plan=${plan.id}&billing=${billingCycle}`);
    } else {
      navigate('/register');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      <Helmet>
        <title>SaaS Management Pricing | Cost Optimization & Licenses</title>
        <meta name="description" content="Explore SaaSGama pricing for SaaS Management, License Management Software, and SaaS Spend Management. Optimize costs and manage subscriptions with ease." />
        <meta name="keywords" content="SaaS Management Pricing, SaaS Cost Optimization, License Management Software, SaaS Spend Management" />
        <link rel="canonical" href="https://saasgama.com/pricing" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://saasgama.com/pricing" />
        <meta property="og:site_name" content="SaaSGama" />
        <meta property="og:title" content="SaaS Management Pricing | Cost Optimization & Licenses" />
        <meta property="og:description" content="Explore SaaSGama pricing for SaaS Management, License Management Software, and SaaS Spend Management. Optimize costs and manage subscriptions with ease." />
        <meta property="og:image" content="https://saasgama.com/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="SaaS Management Pricing | Cost Optimization & Licenses" />
        <meta name="twitter:description" content="Explore SaaSGama pricing for SaaS Management, License Management Software, and SaaS Spend Management. Optimize costs and manage subscriptions with ease." />
        <meta name="twitter:image" content="https://saasgama.com/og-image.png" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            { "@type": "Organization", "@id": "https://saasgama.com/#organization", "name": "SaaSGama", "url": "https://saasgama.com", "logo": "https://saasgama.com/logo.png", "description": "SaaSGama helps organizations manage SaaS subscriptions, software licenses, renewals, vendors, software usage, and spending from a centralized platform." },
            { "@type": "SoftwareApplication", "@id": "https://saasgama.com/pricing#software", "name": "SaaSGama", "url": "https://saasgama.com/pricing", "applicationCategory": "BusinessApplication", "applicationSubCategory": "SaaS Management Platform", "operatingSystem": "Web", "applicationSuite": "SaaSGama", "description": "SaaSGama provides SaaS subscription management, license tracking, spend optimization, renewal monitoring, vendor management, and software usage analytics.", "featureList": ["SaaS Subscription Tracking", "License Management", "Renewal Monitoring", "Software Usage Analytics", "Spend Optimization", "Vendor Management", "Contract Management", "Application Discovery", "Cost Control", "Centralized Dashboard"], "offers": { "@type": "Offer", "url": "https://saasgama.com/pricing", "priceCurrency": "USD", "availability": "https://schema.org/InStock" }, "publisher": { "@id": "https://saasgama.com/#organization" } },
            { "@type": "Service", "@id": "https://saasgama.com/pricing#service", "name": "SaaS Management Software", "serviceType": "SaaS Subscription Management", "provider": { "@id": "https://saasgama.com/#organization" }, "url": "https://saasgama.com/pricing", "description": "Manage software subscriptions, optimize licenses, monitor renewals, reduce SaaS waste, and improve software visibility." },
            { "@type": "OfferCatalog", "name": "SaaSGama Pricing Plans", "itemListElement": [
              { "@type": "Offer", "name": "Single Plan", "price": "25", "priceCurrency": "USD", "availability": "https://schema.org/InStock", "description": "Perfect for small teams and startups with up to 2 users and 50 SaaS applications." },
              { "@type": "Offer", "name": "Team Plan", "price": "39", "priceCurrency": "USD", "availability": "https://schema.org/InStock", "description": "Ideal for growing businesses with advanced reporting, renewal tracking and team management." },
              { "@type": "Offer", "name": "Enterprise Plan", "price": "199", "priceCurrency": "USD", "availability": "https://schema.org/InStock", "description": "Enterprise-grade SaaS management with unlimited licenses, custom reporting and dedicated support." }
            ]},
            { "@type": "BreadcrumbList", "@id": "https://saasgama.com/pricing#breadcrumb", "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://saasgama.com" },
              { "@type": "ListItem", "position": 2, "name": "Pricing", "item": "https://saasgama.com/pricing" }
            ]},
            { "@type": "WebPage", "@id": "https://saasgama.com/pricing#webpage", "url": "https://saasgama.com/pricing", "name": "SaaSGama Pricing", "description": "Explore SaaSGama pricing plans for SaaS subscription tracking, license management, spend optimization, and renewal management.", "breadcrumb": { "@id": "https://saasgama.com/pricing#breadcrumb" }, "about": { "@id": "https://saasgama.com/pricing#software" } },
            { "@type": "FAQPage", "@id": "https://saasgama.com/pricing#faq", "mainEntity": pricingFaqs.map(faq => ({ "@type": "Question", "name": faq.q, "acceptedAnswer": { "@type": "Answer", "text": faq.a } })) }
          ]
        })}</script>
      </Helmet>

      <WebsiteHeader showBackButton />

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-gray-900 via-blue-900 to-indigo-900 text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold mb-6">
              SaaS Management{' '}
              <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                Pricing
              </span>
            </h1>
            <p className="text-base md:text-xl text-gray-300 leading-relaxed max-w-3xl mx-auto">
              Choose the plan that fits your team size and needs. All plans include our core features.
            </p>
          </div>
        </div>
      </section>


      {/* Pricing Cards */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          {/* Billing cycle toggle */}
          <div className="flex items-center justify-center mb-12">
            <div className="inline-flex items-center bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`px-5 py-2 text-sm font-semibold rounded-md transition-colors ${
                  billingCycle === 'monthly' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingCycle('yearly')}
                className={`px-5 py-2 text-sm font-semibold rounded-md transition-colors ${
                  billingCycle === 'yearly' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'
                }`}
              >
                Yearly
              </button>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-4 md:gap-8 max-w-6xl mx-auto">
            {(pricingPlans as PricingPlan[]).map((plan: PricingPlan) => {
              return (
                <div
                  key={plan.id}
                  className={`relative bg-white rounded-2xl border-2 p-4 md:p-8 ${
                    plan.isPopular
                      ? 'border-blue-500 shadow-2xl md:scale-105'
                      : 'border-gray-200 hover:border-blue-300'
                  } transition-all hover:shadow-xl`}
                >
                  {/* Popular badge */}
                  {plan.isPopular && (
                    <div className="absolute -top-3 md:-top-4 left-1/2 transform -translate-x-1/2">
                      <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-4 md:px-6 py-1 md:py-2 rounded-full text-xs md:text-sm font-bold">
                        Most Popular
                      </div>
                    </div>
                  )}

                  <div className="text-center mb-4 md:mb-8">
                    <h3 className="text-lg md:text-2xl font-bold text-gray-800 mb-1 md:mb-2">{plan.name}</h3>
                    <p className="text-gray-600 text-sm md:text-base mb-3 md:mb-6">{plan.description}</p>

                    {billingCycle === 'yearly' && plan.prices.year ? (
                      <div className="mb-3 md:mb-6">
                        <div className="text-2xl md:text-4xl font-bold text-gray-800">
                          ${(plan.prices.year.amount / 100).toFixed(0)}
                          <span className="text-base md:text-lg text-gray-600 font-normal">/year</span>
                        </div>
                        {plan.prices.month && (
                          <div className="mt-1 md:mt-2 text-xs md:text-sm text-green-600 font-medium">
                            ${(plan.prices.year.amount / 100 / 12).toFixed(2)}/month — save ${(plan.prices.month.amount / 100 * 12 - plan.prices.year.amount / 100).toFixed(0)}/year
                          </div>
                        )}
                      </div>
                    ) : plan.prices.month ? (
                      <div className="mb-3 md:mb-6">
                        <div className="text-2xl md:text-4xl font-bold text-gray-800">
                          ${(plan.prices.month.amount / 100).toFixed(0)}
                          <span className="text-base md:text-lg text-gray-600 font-normal">/month</span>
                        </div>
                      </div>
                    ) : null}
                  </div>

                  <ul className="space-y-2 md:space-y-4 mb-4 md:mb-8">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <Check className="w-4 md:w-5 h-4 md:h-5 text-green-500 mr-2 md:mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700 text-sm md:text-base">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    className={`w-full py-2 md:py-3 rounded-xl font-semibold transition-all text-sm md:text-base ${
                      plan.isPopular
                        ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl'
                        : 'bg-gray-100 hover:bg-gray-200 text-gray-800'
                    }`}
                    onClick={() => handleGetStarted(plan)}
                  >
                    Get Your Trial Today
                  </Button>

                  {/* Limited Offer coupon — Single plan only, below CTA */}
                  {plan.name.toLowerCase() === 'single' && (
                    <div className="mt-3 rounded-lg overflow-hidden" style={{ border: '1.5px solid #D4AF37' }}>
                      {/* Top ribbon */}
                      <div className="py-1 px-3 text-center" style={{ background: 'linear-gradient(90deg, #a86200 0%, #D4AF37 50%, #a86200 100%)' }}>
                        <span className="text-white font-bold uppercase tracking-widest" style={{ fontSize: '10px' }}>★ Limited Offer ★</span>
                      </div>
                      {/* Body */}
                      <div className="relative py-2 px-3 text-center" style={{ background: 'linear-gradient(135deg, #fffdf0 0%, #fef9c3 100%)' }}>
                        <span className="absolute top-1 left-1.5 text-yellow-300" style={{ fontSize: '8px' }}>✦</span>
                        <span className="absolute top-1 right-1.5 text-yellow-300" style={{ fontSize: '8px' }}>✦</span>
                        <div className="text-xs text-gray-700">Lifetime access for <span className="font-bold text-gray-900">$79</span> once.</div>
                        <div className="mt-1.5 inline-block border border-dashed rounded px-2 py-0.5" style={{ borderColor: '#c78007' }}>
                          <span className="font-bold text-gray-700 tracking-wide" style={{ fontSize: '10px' }}>Code: <span className="font-mono">LIFETIMESG</span></span>
                        </div>
                      </div>
                    </div>
                  )}

                </div>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <p className="text-gray-600 mb-6">
              No credit card required to get started.
            </p>
            <Button 
              variant="outline" 
              size="lg"
              className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50 px-8 py-3"
              onClick={() => navigate('/contact')}
            >
              Need a custom plan? Contact Sales
            </Button>
          </div>
        </div>
      </section>

      {/* Feature Comparison */}
      <section className="hidden md:block py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-2xl md:text-4xl font-bold mb-6 text-gray-800">Compare Plans</h2>
              <p className="text-base md:text-xl text-gray-600">
                Detailed feature comparison to help you choose the right plan
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Features</th>
                      <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">Single</th>
                      <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">Team</th>
                      <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">Enterprise</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900">Users</td>
                      <td className="px-6 py-4 text-center text-sm text-gray-600">Up to 2 (including admin)</td>
                      <td className="px-6 py-4 text-center text-sm text-gray-600">Up to 4 (including admin)</td>
                      <td className="px-6 py-4 text-center text-sm text-gray-600">Up to 15 (including admin)</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900">Applications</td>
                      <td className="px-6 py-4 text-center text-sm text-gray-600">Up to 50</td>
                      <td className="px-6 py-4 text-center text-sm text-gray-600">Up to 100</td>
                      <td className="px-6 py-4 text-center text-sm text-gray-600">Unlimited</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900">Licenses</td>
                      <td className="px-6 py-4 text-center text-sm text-gray-600">120 license limit</td>
                      <td className="px-6 py-4 text-center text-sm text-gray-600">250 license limit</td>
                      <td className="px-6 py-4 text-center text-sm text-gray-600">Unlimited</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900">License Tracking</td>
                      <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                      <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                      <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900">Cost Optimization</td>
                      <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                      <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                      <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900">Basic Reports</td>
                      <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                      <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                      <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900">Advanced Analytics</td>
                      <td className="px-6 py-4 text-center text-gray-400">—</td>
                      <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                      <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900">Dedicated Support</td>
                      <td className="px-6 py-4 text-center text-gray-400">—</td>
                      <td className="px-6 py-4 text-center text-gray-400">—</td>
                      <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      {showFaq && (
        <section className="py-20 bg-white">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-2xl md:text-4xl font-bold mb-6 text-gray-800">Frequently Asked Questions</h2>
                <p className="text-base md:text-xl text-gray-600">
                  Everything you need to know about our pricing and plans
                </p>
              </div>

              <Accordion type="single" collapsible className="w-full space-y-4">
                {pricingFaqs.map((faq, i) => (
                  <AccordionItem key={i} value={`item-${i}`} className="border border-gray-200 rounded-lg px-6 bg-white shadow-sm hover:shadow-md transition-shadow">
                    <AccordionTrigger className="text-left text-lg font-semibold text-gray-800 hover:text-blue-600">
                      {faq.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-gray-600 pt-2">
                      {faq.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>
      )}

      {/* Customer Success Section */}
      <section className="py-10 md:py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-8 md:mb-16">
            <h2 className="text-2xl md:text-4xl font-bold mb-6 text-gray-800">Trusted by 500+ Companies</h2>
            <p className="text-base md:text-xl text-gray-600">
              See how businesses like yours are saving money with SaaSGama
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-4 md:gap-8 max-w-6xl mx-auto">
            {/* Success Story 1 */}
            <div className="bg-white p-4 md:p-8 rounded-2xl shadow-lg border border-gray-100">
              <div className="flex text-yellow-400 mb-2 md:mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 md:w-5 md:h-5 fill-current" />
                ))}
              </div>
              <p className="text-gray-700 text-sm md:text-base mb-3 md:mb-6">
                "SaaSGama helped us identify $200K in unused licenses. The ROI was immediate and the platform is incredibly easy to use."
              </p>
              <div className="flex items-center">
                <div className="w-9 h-9 md:w-12 md:h-12 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold text-sm mr-3 md:mr-4">SK</div>
                <div>
                  <div className="font-semibold text-gray-800 text-sm md:text-base">Sarah Kim</div>
                  <div className="text-gray-600 text-xs md:text-sm">IT Director, TechFlow</div>
                </div>
              </div>
            </div>

            {/* Success Story 2 */}
            <div className="bg-white p-4 md:p-8 rounded-2xl shadow-lg border border-gray-100">
              <div className="flex text-yellow-400 mb-2 md:mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 md:w-5 md:h-5 fill-current" />
                ))}
              </div>
              <p className="text-gray-700 text-sm md:text-base mb-3 md:mb-6">
                "The automated renewal tracking alone is worth the subscription cost. We never miss a renewal deadline anymore."
              </p>
              <div className="flex items-center">
                <div className="w-9 h-9 md:w-12 md:h-12 bg-green-500 rounded-full flex items-center justify-center text-white font-bold text-sm mr-3 md:mr-4">MR</div>
                <div>
                  <div className="font-semibold text-gray-800 text-sm md:text-base">Michael Rodriguez</div>
                  <div className="text-gray-600 text-xs md:text-sm">CFO, StartupCo</div>
                </div>
              </div>
            </div>

            {/* Success Story 3 */}
            <div className="bg-white p-4 md:p-8 rounded-2xl shadow-lg border border-gray-100">
              <div className="flex text-yellow-400 mb-2 md:mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 md:w-5 md:h-5 fill-current" />
                ))}
              </div>
              <p className="text-gray-700 text-sm md:text-base mb-3 md:mb-6">
                "Enterprise-grade features at a fraction of the cost. SaaSGama scales perfectly with our growing team."
              </p>
              <div className="flex items-center">
                <div className="w-9 h-9 md:w-12 md:h-12 bg-purple-500 rounded-full flex items-center justify-center text-white font-bold text-sm mr-3 md:mr-4">LC</div>
                <div>
                  <div className="font-semibold text-gray-800 text-sm md:text-base">Lisa Chen</div>
                  <div className="text-gray-600 text-xs md:text-sm">VP Operations, GrowthCorp</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
        <div className="container mx-auto px-6 text-center">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-5xl font-bold mb-6">
              Ready to start saving?
            </h2>
            <p className="text-base md:text-xl mb-8 text-blue-100">
              Join 500+ companies already optimizing their SaaS spending with SaaSGama
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg"
                className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 text-lg font-semibold rounded-xl shadow-lg"
                asChild
              >
                <Link href="/register" className="flex items-center">
                  Get Started
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </Button>
              <Button 
                variant="outline"
                size="lg"
                className="border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white px-8 py-4 text-lg font-semibold rounded-xl transition-all bg-white"
                asChild
              >
                <Link href="/contact">Talk to Sales</Link>
              </Button>
            </div>

            <div className="mt-8 text-blue-200 text-sm">
              No credit card required • Cancel anytime • Setup in 5 minutes
            </div>
          </div>
        </div>
      </section>

      <WebsiteFooter />
    </div>
  );
}
